import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { CreateLines } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { LinesMesh } from "@babylonjs/core/Meshes/linesMesh";

const VALID = new Color3(0.4, 0.9, 0.5);
const INVALID = new Color3(0.9, 0.35, 0.3);

/**
 * A ground landing disc plus a parabolic aim arc, used for both teleport and
 * plant aiming. Colour signals target validity. Purely visual — no camera writes.
 */
export class AimReticle {
  private readonly disc: Mesh;
  private readonly discMat: StandardMaterial;
  private arc: LinesMesh | null = null;
  private readonly scene: Scene;
  private readonly color: Color3;
  private readonly withArc: boolean;

  /** `withArc`: draw the parabolic aim arc. Off in VR, where a straight laser
   * already shows the aim (two lines would read as two targets). */
  constructor(scene: Scene, name: string, tint: Color3 = VALID, withArc = true) {
    this.scene = scene;
    this.color = tint;
    this.withArc = withArc;
    this.disc = CreateDisc(`${name}Disc`, { radius: 0.35, tessellation: 32 }, scene);
    this.disc.rotation.x = Math.PI / 2; // lie flat on the ground
    this.discMat = new StandardMaterial(`${name}DiscMat`, scene);
    this.discMat.disableLighting = true;
    this.discMat.emissiveColor = tint;
    this.discMat.alpha = 0.6;
    this.disc.material = this.discMat;
    this.disc.isPickable = false;
    this.disc.setEnabled(false);
  }

  hide(): void {
    this.disc.setEnabled(false);
    this.arc?.setEnabled(false);
  }

  /** Recolour the "valid" tint (e.g. to the selected seed's colour). */
  setTint(c: Color3): void {
    this.color.copyFrom(c);
  }

  /** Show the reticle at `target`, with an arc from `from`. `valid` sets colour. */
  show(from: Vector3, target: Vector3, valid: boolean): void {
    this.discMat.emissiveColor = valid ? this.color : INVALID;
    this.disc.position.set(target.x, target.y + 0.03, target.z);
    this.disc.setEnabled(true);
    if (this.withArc) this.updateArc(from, target, valid);
  }

  private updateArc(from: Vector3, target: Vector3, valid: boolean): void {
    const pts: Vector3[] = [];
    const n = 20;
    const apex = Math.max(from.y, target.y) + 1.2;
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const x = from.x + (target.x - from.x) * t;
      const z = from.z + (target.z - from.z) * t;
      // parabola peaking mid-flight
      const y = from.y + (target.y - from.y) * t + (apex - from.y) * 4 * t * (1 - t) * 0.5;
      pts.push(new Vector3(x, y, z));
    }
    this.arc = this.arc
      ? CreateLines("aimArc", { points: pts, instance: this.arc, updatable: true }, this.scene)
      : CreateLines("aimArc", { points: pts, updatable: true }, this.scene);
    this.arc.color = valid ? this.color : INVALID;
    this.arc.isPickable = false;
    this.arc.setEnabled(true);
  }
}
